import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Button,
  Box,
  IconButton,
  FormControlLabel,
  Switch,
  Tooltip,
  Chip,
  Paper,
  Divider,
  Fade,
  Grow,
  Badge,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import FolderIcon from '@mui/icons-material/FolderSpecial';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import BrowseIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import settingFoldersConnectorIcon from '../images/folders.png';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from './WebSocketContext';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useFolderPicker } from './useFolderPicker';
import CloseButton from './Common/CloseButton';

const SettingFoldersConnector = ({ caseid }) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages } = useWebSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Calculate responsive sizes
  const iconSize = isMobile ? 32 : isTablet ? 38 : 42;
  const containerPadding = isMobile ? '6px' : '8px';
  const progressSize = isMobile ? 44 : isTablet ? 52 : 58;
  const progressOffset = isMobile ? '-6px' : '-8px';
  const captionFontSize = isMobile ? '0.7rem' : '0.75rem';

  const [foldersEnabled, setFoldersEnabled] = useState(false);
  const [folders, setFolders] = useState([]);
  const [isChanged, setIsChanged] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false); // Added for loading animations
  const [folderAdded, setFolderAdded] = useState(false); // For success animation
  const [restrictToUserFolder, setRestrictToUserFolder] = useState(false);
  const [allowedRoots, setAllowedRoots] = useState([]);
  const [displayRoot, setDisplayRoot] = useState('');

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const formatFolderForDisplay = useCallback((path) => {
    if (!restrictToUserFolder || !displayRoot || !path) return path;
    const normalize = (p) => p.replace(/\\/g, '/');
    const normRoot = normalize(displayRoot).toLowerCase();
    const normPath = normalize(path);
    const normPathLower = normPath.toLowerCase();

    if (normPathLower.startsWith(normRoot)) {
      const rel = normPath.substring(normRoot.length).replace(/^\\\/?/, '').replace(/^\/?/, '');
      return `/${rel || ''}`;
    }
    return path;
  }, [restrictToUserFolder, displayRoot]);

  // Define theme colors for consistent styling
  const themeColors = {
    primary: '#5757b0',
    primaryLight: '#7878c5',
    primaryDark: '#3a3a8c',
    accent: '#ff8a65',
    text: '#333',
    lightText: '#f5f5f5',
    background: '#f8f9fa',
    cardBackground: '#ffffff'
  };

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    ws,
    onFolderSelected: (folderPath) => {
      if (folderPath && !folders.includes(folderPath)) {
        setFolders(prev => [...prev, folderPath]);
        setIsChanged(true);
        setFolderAdded(true);
        setTimeout(() => setFolderAdded(false), 2000);
      }
      setLoading(false); // Ensure loading is reset after folder selection
    },
    onFolderBrowserClose: () => {
      setLoading(false);
    },
    restrictToUserFolder: restrictToUserFolder,
    allowedRoots: allowedRoots,
    displayRoot: displayRoot
  });

  useEffect(() => {
    if (ws) {
      setLoading(true);
      ws.send(JSON.stringify({ command: 'getFoldersSettings', caseid }));
    }
  }, [ws, caseid]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      try {
        // Message is already parsed in WebSocketContext
        if (parsedMessage.command === 'getFoldersSettings') {
          const data = parsedMessage.data || {};
          setFoldersEnabled(data.enable === 1);
          setFolders(data.folders || []);
          setRestrictToUserFolder(data.restrictToUserFolder || false);
          setAllowedRoots(data.allowedRoots || []);
          setDisplayRoot(data.displayRoot || '');
          setIsChanged(false);
          setLoading(false);
        } else if (parsedMessage.command === 'browsedirectory') {
          const folder = parsedMessage.data.folder;
          if (folder && !folders.includes(folder)) {
            setFolders(prev => [...prev, folder]);
            setIsChanged(true);
            setFolderAdded(true);
            // Reset success animation after 2 seconds
            setTimeout(() => setFolderAdded(false), 2000);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, folders]);

  const handleFoldersToggle = (enabled) => {
    setFoldersEnabled(enabled);
    setIsChanged(true);
    if (ws) {
      ws.send(JSON.stringify({ command: 'enableFolders', enable: enabled ? '1' : '0', caseid }));
    }
  };

  const handleDeleteFolder = (index) => {
    setFolders(prev => prev.filter((_, i) => i !== index));
    setIsChanged(true);
  };

  const handleSaveFolders = () => {
    setLoading(true);
    if (ws) {
      ws.send(JSON.stringify({ command: 'saveFoldersSettings', caseid, folders }));
    }
    // Using setTimeout to show loading animation briefly
    setTimeout(() => {
      setLoading(false);
      setIsChanged(false);
      handleClose();
    }, 800);
  };

  // Rename the local handler to avoid redeclaration
  const onBrowseDirectoryClick = () => {
    setLoading(true);
    handleBrowseDirectory();
  };

  const handleOpenFolder = (folderPath) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ command: 'openfolderpath', folder: folderPath }));
    }
  };

  return (
    <Box 
      p={1} 
      width={{ xs: '100%', sm: 130, md: 150 }}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
        }
      }}
    >
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center"
      >
        <Tooltip 
          title={t("folders_connector.title")}
          arrow
          placement="top"
        >
          <Badge 
            color="success" 
            variant="dot" 
            invisible={!foldersEnabled} 
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <div className="icon-container" style={{ 
              position: 'relative', 
              cursor: 'pointer',
              background: 'rgba(245, 245, 245, 0.8)',
              borderRadius: '50%',
              padding: containerPadding,
              boxShadow: foldersEnabled ? '0 0 15px rgba(87, 87, 176, 0.6)' : 'none',
              transition: 'all 0.3s ease'
            }}>
              <img
                src={settingFoldersConnectorIcon}
                alt="Folders Connector Status"
                style={{ 
                  width: iconSize, 
                  height: iconSize, 
                  filter: foldersEnabled ? 'none' : 'grayscale(100%)',
                  transition: 'all 0.3s ease',
                  transform: foldersEnabled ? 'scale(1.05)' : 'scale(1)'
                }}
                onClick={handleOpen}
              />
              {loading && (
                <CircularProgress 
                  size={progressSize} 
                  style={{ 
                    position: 'absolute', 
                    top: progressOffset, 
                    left: progressOffset, 
                    color: themeColors.primary 
                  }} 
                />
              )}
            </div>
          </Badge>
        </Tooltip>
        <Typography 
          variant="caption" 
          sx={{ 
            mt: 1, 
            fontSize: captionFontSize,
            textAlign: 'center',
            color: foldersEnabled ? themeColors.primary : 'text.secondary',
            fontWeight: foldersEnabled ? 'medium' : 'normal',
            transition: 'all 0.3s ease'
          }}
        >
          {t("folder_connector.caption")}
        </Typography>
      </Box>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 400 }}
        PaperProps={{
          elevation: 5,
          sx: {
            borderRadius: 2,
            background: `linear-gradient(to right bottom, ${themeColors.cardBackground}, ${themeColors.background})`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryDark} 100%)`,
            color: themeColors.lightText,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <FolderIcon sx={{ mr: 1 }} />
          {t("folders_connector.title")}
          <CloseButton
            ariaLabel="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: themeColors.lightText
            }}
          />
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              mb: 3, 
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SettingsIcon sx={{ color: themeColors.primary, mr: 1.5 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={foldersEnabled}
                      onChange={(e) => handleFoldersToggle(e.target.checked)}
                      color="primary"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: themeColors.primary,
                          '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.08)' }
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: themeColors.primaryLight
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 'medium', color: themeColors.text }}>
                      {t('folder_connector.enable_connector')}
                    </Typography>
                  }
                />
              </Box>
              <Tooltip title={t("folders_connector.enable_connector_tooltip")} arrow>
                <IconButton sx={{ color: themeColors.primary }}>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          {foldersEnabled && (
            <Grow in={foldersEnabled} timeout={500}>
              <div>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    mb: 3, 
                    minHeight: '120px',
                    maxHeight: '250px',
                    overflowY: 'auto',
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.5)',
                    border: '1px solid rgba(200, 200, 200, 0.3)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignContent: 'flex-start'
                  }}
                >
                  {folders.length === 0 ? (
                    <Box 
                      sx={{ 
                        width: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100px',
                        opacity: 0.7
                      }}
                    >
                      <FolderIcon sx={{ fontSize: 40, color: themeColors.primaryLight, mb: 1 }} />
                      <Typography variant="body2" color="textSecondary">
                        {t('folder_connector.no_folders')}
                      </Typography>
                    </Box>
                  ) : (
                    folders.map((folder, index) => (
                      <Grow key={index} in={true} timeout={(index + 1) * 200}>
                        <Paper
                          elevation={1}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            p: 1,
                            mb: 1,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: `1px solid ${themeColors.primaryLight}20`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              transform: 'translateY(-2px)',
                              backgroundColor: 'rgba(255, 255, 255, 1)',
                              borderColor: `${themeColors.primaryLight}50`
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                            <FolderIcon sx={{ 
                              color: themeColors.primary,
                              fontSize: 22,
                              mr: 1.5
                            }} />
                            <Typography
                              variant="body2"
                            sx={{
                              color: themeColors.text,
                              fontWeight: 'medium',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap'
                            }}
                          >
                              {formatFolderForDisplay(folder)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip title={t('folder_connector.open_folder')} arrow>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenFolder(folder)}
                                sx={{
                                  color: themeColors.primary,
                                  mr: 0.5,
                                  '&:hover': {
                                    color: themeColors.primaryDark,
                                    backgroundColor: `${themeColors.primaryLight}15`
                                  }
                                }}
                              >
                                <FolderOpenIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('folder_connector.remove_folder')} arrow>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteFolder(index)}
                                sx={{
                                  color: '#f44336',
                                  '&:hover': {
                                    color: '#d32f2f',
                                    backgroundColor: 'rgba(244, 67, 54, 0.08)'
                                  }
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Paper>
                      </Grow>
                    ))
                  )}
                </Paper>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <BrowseIcon />}
                    onClick={onBrowseDirectoryClick}
                    disabled={loading}
                    sx={{
                      backgroundColor: themeColors.primary,
                      color: themeColors.lightText,
                      '&:hover': { backgroundColor: themeColors.primaryLight },
                      py: 1.2,
                      px: 3,
                      borderRadius: 2,
                      boxShadow: '0 4px 10px rgba(87, 87, 176, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        transform: 'translateY(-3px)',
                        boxShadow: '0 6px 12px rgba(87, 87, 176, 0.4)',
                      },
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {folderAdded ? (
                      <Fade in={folderAdded}>
                        <Box component="span">
                          {t('folder_connector.folder_added')}
                        </Box>
                      </Fade>
                    ) : (
                      t('folder_connector.add_folder')
                    )}
                  </Button>
                </Box>
              </div>
            </Grow>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          backgroundColor: 'rgba(245, 245, 245, 0.5)',
          backdropFilter: 'blur(5px)',
          borderTop: '1px solid rgba(200, 200, 200, 0.3)',
          px: 3,
          py: 2
        }}>
          {foldersEnabled && (
            <>
              <Button
                variant="outlined"
                onClick={handleClose}
                startIcon={<CloseIcon />}
                sx={{
                  color: themeColors.primary,
                  borderColor: themeColors.primary,
                  '&:hover': { 
                    borderColor: themeColors.primaryLight,
                    backgroundColor: 'rgba(87, 87, 176, 0.04)'
                  },
                  borderRadius: 2,
                  px: 3
                }}
              >
                {t('close')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveFolders}
                disabled={!isChanged || loading}
                sx={{
                  backgroundColor: themeColors.primary,
                  color: themeColors.lightText,
                  '&:hover': { backgroundColor: themeColors.primaryLight },
                  '&.Mui-disabled': { 
                    backgroundColor: 'rgba(87, 87, 176, 0.4)', 
                    color: 'rgba(255, 255, 255, 0.6)' 
                  },
                  borderRadius: 2,
                  px: 3,
                  ml: 2,
                  boxShadow: '0 4px 10px rgba(87, 87, 176, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:not(.Mui-disabled):hover': { 
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 12px rgba(87, 87, 176, 0.4)',
                  },
                }}
              >
                {t('folder_connector.save_folders')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {FolderBrowserComponent}
    </Box>
  );
};

export default SettingFoldersConnector;
